import React from 'react';
import { Award, Target, Heart, Shield, CheckCircle } from 'lucide-react';
import { aboutContent, stats } from '../mockData';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About ApprentCal
            </h1>
            <p className="text-xl text-blue-100">
              Your trusted partner for precision electrical testing and calibration services in Bihar and Eastern India.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center p-6 bg-gradient-to-br from-blue-50 to-white rounded-lg border border-blue-100">
                <div className="text-3xl md:text-4xl font-bold text-blue-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Our Mission</h2>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {aboutContent.mission}
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Our Vision</h2>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {aboutContent.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600">
              The principles that guide our work every day
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {aboutContent.values.map((value, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-blue-50 to-white p-6 rounded-lg border border-blue-100 flex items-start gap-4"
              >
                <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <p className="text-gray-700 font-medium">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Shield className="w-10 h-10 text-blue-600" />
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                Certifications & Accreditations
              </h2>
            </div>
            <p className="text-xl text-gray-600">
              Committed to maintaining the highest industry standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {aboutContent.certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md border border-gray-200 text-center hover:shadow-xl transition-shadow"
              >
                <Award className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <p className="text-gray-700 font-semibold">{cert}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-900 to-blue-700 text-white p-8 md:p-12 rounded-lg shadow-xl">
              <h2 className="text-3xl font-bold mb-6">About Our Laboratory</h2>
              <div className="space-y-4 text-blue-100">
                <p className="leading-relaxed">
                  ApprentCal Testing & Calibration Laboratory is a premier electrical testing and calibration facility located in Buxar, Bihar. Established with a commitment to technical excellence, we serve industries across Eastern India with comprehensive electrical testing and instrument calibration services.
                </p>
                <p className="leading-relaxed">
                  Under the leadership of <span className="text-white font-semibold">Mr. Amit Kumar</span>, our laboratory has built a reputation for accuracy, reliability, and adherence to international standards. We utilize state-of-the-art equipment and employ highly trained technicians to ensure every test and calibration meets the strictest quality requirements.
                </p>
                <p className="leading-relaxed">
                  Our laboratory is equipped with advanced testing instruments capable of handling a wide range of electrical equipment, from high-voltage transformers to precision measurement devices. We provide NABL-traceable calibration certificates and detailed test reports that comply with industry regulations.
                </p>
                <p className="leading-relaxed">
                  Whether you need routine calibration, comprehensive electrical testing, or safety compliance audits, ApprentCal is your trusted partner for maintaining the integrity and safety of your electrical systems.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;